import React from 'react';

/**
 * The icon set.
 *
 * Replaces the Unicode glyphs the navigation and pages were using (◎ ⚑ ✦ ≡ ◈
 * ⊞ ▶ ▤ ⊙ ▾). Those render from a different font on every operating system,
 * sit off the text baseline, cannot take a stroke weight and do not scale with
 * their label — and ⚑ was doing duty for two different nav items.
 *
 * Drawn in the Lucide style (24×24 box, round caps and joins) so the set can
 * be swapped for `lucide-react` later without touching a single call site.
 * Kept as a local component for now because it adds no dependency.
 *
 *     <Icon name="spark" />
 *     <Icon name="shield" size={13} strokeWidth={2.2} />
 */

const PATHS = {
  spark:   <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />,
  shield:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></>,
  image:   <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.7-3.7a2 2 0 0 0-2.8 0L6 20" /></>,
  target:  <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" /></>,
  user:    <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  check:   <path d="M20 6 9 17l-5-5" />,
  alert:   <><circle cx="12" cy="12" r="9" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
  info:    <><circle cx="12" cy="12" r="9" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>,
  right:   <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>,
  down:    <path d="m6 9 6 6 6-6" />,
  up:      <path d="m18 15-6-6-6 6" />,
  chevron: <path d="m9 18 6-6-6-6" />,
  plus:    <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  minus:   <path d="M5 12h14" />,
  clock:   <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  cards:   <><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></>,
  x:       <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
  block:   <><circle cx="12" cy="12" r="9" /><path d="m5.6 5.6 12.8 12.8" /></>,
  search:  <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  download: <><path d="M12 3v12" /><path d="m7 11 5 5 5-5" /><path d="M5 21h14" /></>,
  pin:     <><path d="M12 17v5" /><path d="M9 10.8V4h6v6.8l2 2.2H7l2-2.2Z" /></>,
  layers:  <><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 17 9 5 9-5" /><path d="m3 12 9 5 9-5" /></>,

  /* Added for the studio: a layer list needs a glyph per role, a visibility
     toggle and a delete that is not the same × used for closing a dialog. */
  text:    <><path d="M4 6V4h16v2" /><path d="M12 4v16" /><path d="M8 20h8" /></>,
  square:  <rect x="3" y="3" width="18" height="18" rx="2" />,
  eye:     <><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>,
  hide:    <><path d="M10.6 6.2A9.6 9.6 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3 3.9" /><path d="M6.2 6.4A17 17 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4.3-1" /><path d="m3 3 18 18" /></>,
  trash:   <><path d="M4 7h16" /><path d="M9 7V5h6v2" /><path d="M6 7l1 13h10l1-13" /></>,
  undo:    <><path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></>,
  redo:    <><path d="m15 14 5-5-5-5" /><path d="M20 9H10a6 6 0 0 0 0 12h3" /></>,
  frame:   <><path d="M4 8h16" /><path d="M4 16h16" /><path d="M8 4v16" /><path d="M16 4v16" /></>,
};

export default function Icon({ name, size = 16, strokeWidth = 2, className, style }) {
  const path = PATHS[name];
  if (!path) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flex: 'none', ...style }}
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}